import { createAsyncThunk, createSlice, miniSerializeError, SerializedError } from "@reduxjs/toolkit";
import { Template } from "src/components/pages/templates/templateDisplayCard";
import { InspectionTemplate } from "src/data/types";
import { apiRequest } from "src/utils/apiRequest";
import { LOADING_STATE } from "src/constants/constants";

export type InspectionTemplateState = {
  inspectionTemplates: Template[];
  selectedInspectionTemplate: InspectionTemplate | null;
  
  // Loading states
  getAllInspectionTemplatesLoading: LOADING_STATE;
  getTemplateByIdLoading: LOADING_STATE;
  
  // Error states
  getAllInspectionTemplatesError: SerializedError | null;
  getTemplateByIdError: SerializedError | null;
};

const initialState: InspectionTemplateState = {
  inspectionTemplates: [],
  selectedInspectionTemplate: null,
  
  // Initialize loading states
  getAllInspectionTemplatesLoading: "idle",
  getTemplateByIdLoading: "idle",
  
  // Initialize error states
  getAllInspectionTemplatesError: null,
  getTemplateByIdError: null,
};

export const getAllInspectionTemplates = createAsyncThunk(
  "inspectionTemplate/getAllInspectionTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiRequest({ url: "/templates", method: "GET" });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);

export const getTemplateById = createAsyncThunk(
  "inspectionTemplate/getTemplateById",
  async (templateId: string, { rejectWithValue }) => {
    try {
      const data = await apiRequest({
        url: `/templates/${templateId}`,
        method: "GET",
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch template");
    }
  }
);

/** Inspection Template Slice */
const { reducer, actions } = createSlice({
  name: "inspectionTemplate",
  initialState,
  reducers: {
    setSelectedInspectionTemplate: (state, { payload }) => {
      state.selectedInspectionTemplate = payload;
    },
  },
  extraReducers: (builder) => {
    // getAllInspectionTemplates
    builder.addCase(getAllInspectionTemplates.pending, (state) => {
      state.getAllInspectionTemplatesLoading = "pending";
      state.getAllInspectionTemplatesError = null;
    });
    builder.addCase(
      getAllInspectionTemplates.fulfilled,
      (state, { payload }) => {
        state.inspectionTemplates = payload;
        state.getAllInspectionTemplatesLoading = "fulfilled";
      }
    );
    builder.addCase(getAllInspectionTemplates.rejected, (state, { error }) => {
      state.getAllInspectionTemplatesLoading = "rejected";
      state.getAllInspectionTemplatesError = miniSerializeError(error);
    });
    
    // getTemplateById
    builder.addCase(getTemplateById.pending, (state) => {
      state.getTemplateByIdLoading = "pending";
      state.getTemplateByIdError = null;
    });
    builder.addCase(getTemplateById.fulfilled, (state, { payload }) => {
      state.selectedInspectionTemplate = payload;
      state.getTemplateByIdLoading = "fulfilled";
    });
    builder.addCase(getTemplateById.rejected, (state, { error }) => {
      state.getTemplateByIdLoading = "rejected";
      state.getTemplateByIdError = miniSerializeError(error);
    });
  },
});

export const { setSelectedInspectionTemplate } = actions;

export default reducer;
